import { memo } from 'react';

import {
  BFCM_PLAY_KIT_LARGE_DISCOUNT_RATE,
  PUF_PLAY_KIT_DISCOUNT_RATE,
} from '@/core/constants';

import useLocale from '@/hooks/useLocale';

import { InventoryStatusCode, ShopifyVariant } from '@/lib/shopify';

import { checkIsBabyAgeRange } from '@/utils/checkIsBabyAgeRange';
import { getLanguageFromLocale } from '@/utils/translations/localizationHelpers';
import { Language } from '@/utils/translations/types/TypeLanguagesAndLocales';

export interface OfferSchemaProps {
  variant: ShopifyVariant;
  url: string;
  isPlayKit?: boolean;
  pufDiscountPlayKitPdp: 'off' | 'hold' | 'control' | 'treatment';
  ageRange?: string;
  inventoryStatus?: InventoryStatusCode | null;
}

function _getStandaloneNameLocalized(language: Language) {
  switch (language) {
    case Language.English:
      return 'Single Play Kit Purchase (no subscription)';
    case Language.French:
      return 'Achat unique d’un Kit d’éveil (sans abonnement)';
    case Language.Dutch:
      return 'Eenmalige aankoop van een Speelkit (zonder abonnement)';
    case Language.Spanish:
      return 'Compra única de un Kit de Juego (sin suscripción)';
    case Language.German:
      return 'Einmaliger Kauf eines Spielzeugsets (kein Abo)';
    default:
      throw new Error('missing translation');
  }
}

function _getSubscriptionNameLocalized(language: Language) {
  switch (language) {
    case Language.English:
      return 'Play Kit Subscription (Pay Per Kit)';
    case Language.French:
      return 'Abonnement aux Kits d’éveil (Paiement à la livraison)';
    case Language.Dutch:
      return 'Abonnement op de Speelkits (Betaling per levering)';
    case Language.Spanish:
      return 'Suscripción a los Kits de Juego (Pago por kit)';
    case Language.German:
      return 'Abo für die Spielzeugsets (Bezahlung pro Set)';
    default:
      throw new Error('missing translation');
  }
}

function _formatPrice(price: string | number | undefined) {
  if (!price) return '0.00';

  const priceNum = typeof price === 'string' ? parseFloat(price) : price;

  if (isNaN(priceNum)) return '0.00';

  return priceNum.toFixed(2);
}

function _getAvailability(
  variant: ShopifyVariant,
  inventoryStatus?: InventoryStatusCode | null,
): string {
  // Use the computed inventory status if provided (more accurate)
  if (inventoryStatus) {
    switch (inventoryStatus) {
      case InventoryStatusCode.OUT_OF_STOCK:
        return 'https://schema.org/OutOfStock';
      case InventoryStatusCode.BACKORDER:
        return 'https://schema.org/BackOrder';
      case InventoryStatusCode.PRESALE:
        return 'https://schema.org/PreOrder';
      case InventoryStatusCode.ACTIVE:
      default:
        return 'https://schema.org/InStock';
    }
  }

  // Fallback to variant-based detection when inventoryStatus is not provided.
  // NOTE: This fallback cannot distinguish between BACKORDER and PRESALE since
  // it lacks access to product tags. Products with the "pre-order" tag will
  // incorrectly return BackOrder instead of PreOrder. Always provide
  // inventoryStatus (via getProductInventoryStatus) for accurate results.
  if (!variant.availableForSale) {
    return 'https://schema.org/OutOfStock';
  }

  // currentlyNotInStock means the item is on backorder or pre-order
  // (but without product tags, we default to backorder)
  if (variant.currentlyNotInStock) {
    return 'https://schema.org/BackOrder';
  }

  return 'https://schema.org/InStock';
}

const OfferSchema = ({
  variant,
  url,
  isPlayKit,
  pufDiscountPlayKitPdp,
  ageRange,
  inventoryStatus,
}: OfferSchemaProps) => {
  const availability = _getAvailability(variant, inventoryStatus);

  const locale = useLocale();

  const language = getLanguageFromLocale(locale);

  const bfcmDiscountPlayKitPdpLargeEnabled = ['hold', 'control'].includes(
    pufDiscountPlayKitPdp,
  );

  const pufDiscountPlayKitPdpEnabled = pufDiscountPlayKitPdp === 'treatment';

  let variantPrice = variant.price?.amount;

  if (isPlayKit && variant.price?.amount) {
    let discountRate = 0;

    if (bfcmDiscountPlayKitPdpLargeEnabled) {
      // The discounted price is calculated based on the age range and region
      const ageRangeKey = checkIsBabyAgeRange(ageRange) ? 'baby' : 'toddler';

      discountRate =
        BFCM_PLAY_KIT_LARGE_DISCOUNT_RATE[
          process.env
            .NEXT_PUBLIC_REGION as keyof typeof BFCM_PLAY_KIT_LARGE_DISCOUNT_RATE
        ][ageRangeKey];
    }

    if (pufDiscountPlayKitPdpEnabled) {
      // The discounted price is calculated based on the region
      discountRate =
        PUF_PLAY_KIT_DISCOUNT_RATE[
          process.env
            .NEXT_PUBLIC_REGION as keyof typeof PUF_PLAY_KIT_DISCOUNT_RATE
        ];
    }

    variantPrice = Math.round(variant.price?.amount * (1 - discountRate));
  }

  const formattedVariantPrice = _formatPrice(variantPrice);

  const variantCurrency = variant.price?.currencyCode;

  const hasStandalonePrice =
    typeof variant.priceCentsStandalone !== 'undefined';

  if (isPlayKit && hasStandalonePrice) {
    const standalonePrice = _formatPrice(
      variant.priceCentsStandalone ? variant.priceCentsStandalone / 100 : 0,
    );

    const standaloneName = _getStandaloneNameLocalized(language);

    const subscriptionName = _getSubscriptionNameLocalized(language);

    return (
      <>
        <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
          <meta itemProp="name" content={standaloneName} />
          <meta itemProp="price" content={standalonePrice} />
          <meta itemProp="priceCurrency" content={variantCurrency} />
          <meta itemProp="url" content={url} />
          <link itemProp="availability" href={availability} />
          <meta
            itemProp="itemCondition"
            content="https://schema.org/NewCondition"
          />
        </div>
        <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
          <meta itemProp="name" content={subscriptionName} />
          <meta itemProp="price" content={formattedVariantPrice} />
          <meta itemProp="priceCurrency" content={variantCurrency} />
          <meta itemProp="url" content={url} />
          <link itemProp="availability" href={availability} />
          <meta
            itemProp="itemCondition"
            content="https://schema.org/NewCondition"
          />
        </div>
      </>
    );
  }

  return (
    <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
      <meta itemProp="price" content={formattedVariantPrice} />
      <meta itemProp="priceCurrency" content={variantCurrency} />
      <meta itemProp="url" content={url} />
      <link itemProp="availability" href={availability} />
    </div>
  );
};

export default memo(OfferSchema);
